import { GanttMilestone } from '../../types/gantt.types'
import { MILESTONE_COLORS, MILESTONE_DEFAULT_COLOR } from '../../constants/gantt.const'
// sticky 날짜 헤더 + 마일스톤 행

type GanttDay = { lbl: string; dow: string; y: number; m: number; su: boolean; sa: boolean; td: boolean; holiday: boolean; }

/** 날짜 칸을 연속된 같은 달끼리 묶는다(달 표시 줄) — 시작 칸 위치·칸 수 */
function monthSpans(days: GanttDay[]): { y: number; m: number; start: number; count: number }[] {
  const spans: { y: number; m: number; start: number; count: number }[] = []
  days.forEach((d, i) => {
    const last = spans[spans.length - 1]
    if (last && last.y === d.y && last.m === d.m) last.count++
    else spans.push({ y: d.y, m: d.m, start: i, count: 1 })
  })
  return spans
}

export type GanttHeaderRowsProps = {
  DAYS: GanttDay[]
  DW: number
  milestoneRowH: number
  milestoneDayMap: Map<number, GanttMilestone[]>
  draggingMsId: number | null
  draggingMsDelta: number
  onMilestoneLabelMouseDown: (e: React.MouseEvent, msId: number, day: number) => void
}

export function GanttHeaderRows({
  DAYS, DW, milestoneRowH, milestoneDayMap, draggingMsId, draggingMsDelta, onMilestoneLabelMouseDown,
}: GanttHeaderRowsProps) {
  return (
    <>
      <div style={{ display: 'flex', background: 'var(--shell-bg)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', position: 'sticky', top: 0, zIndex: 3 }}>
        <div data-tour="col-header" style={{ width: 212, flexShrink: 0, display: 'flex', position: 'sticky', left: 0, zIndex: 4, background: 'var(--shell-bg)' }}>
          <div style={{ width: 160, padding: '8px 12px', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center' }}>공종</div>
          <div style={{ width: 52, padding: '8px 4px', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>소요일</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* 달 표시 줄 — 같은 달 날짜 칸 위에 한 칸으로 'M월'(연도는 표시하지 않음). 글자는 왼쪽 고정 열 바로 옆에 붙어 가로 스크롤해도 보인다 */}
        <div style={{ display: 'flex', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}>
          {monthSpans(DAYS).map(s => {
            const w = s.count * DW
            const label = w >= 28 ? `${s.m}월` : ''
            return (
              <div key={`${s.y}-${s.m}-${s.start}`} style={{ width: w, flexShrink: 0, borderRight: 'var(--border-width-hairline) solid var(--shell-border)', padding: 'var(--spacing-02) 0' }}>
                <span style={{ position: 'sticky', left: 212, display: 'inline-block', padding: '0 var(--spacing-03)', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface)', whiteSpace: 'nowrap' }}>{label}</span>
              </div>
            )
          })}
        </div>
        <div data-tour="days-header" style={{ display: 'flex' }}>
        {DAYS.map((d, i) => {
          return (
            <div key={i} style={{ width: DW, flexShrink: 0, textAlign: 'center', padding: '5px 0 3px', fontSize: 'var(--type-caption-size)', color: d.td ? 'var(--gt-primary)' : (d.su || d.holiday) ? 'var(--status-danger-600)' : d.sa ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, background: d.td ? 'var(--gt-subtle)' : (d.su || d.sa || d.holiday) ? 'var(--alpha-black-50)' : 'transparent', fontWeight: d.td ? 700 : 400, position: 'relative', overflow: 'visible' }}>
              {DW >= 26 && <span>{d.lbl}</span>}
              {DW >= 14 && DW < 26 && <span style={{ fontSize: 'var(--type-caption-size)' }}>{d.lbl.split('/')[1]}</span>}
              {DW >= 26 && <span style={{ fontSize: 'var(--type-caption-size)' }}>{d.dow}</span>}
              {DW >= 14 && DW < 26 && <span style={{ fontSize: 'var(--type-caption-size)' }}>{d.dow}</span>}
            </div>
          )
        })}
        </div>
        </div>
        <div style={{ flex: 1 }} />
      </div>

      {/* Milestone row — task row와 동일하게 세로 sticky를 쓰지 않는다. sticky는 스크롤 컨테이너의
          가로 overflow:hidden 클리핑을 깨뜨려, 화면 밖 마일스톤이 옆 패널/왼쪽 고정 열 위로
          삐져나와 보이는 문제가 있었다. task bar처럼 왼쪽 고정 열(zIndex 3)보다 낮은
          zIndex로 그 아래에 자연스럽게 가려지도록 한다. */}
      <div data-tour="milestone-row" style={{ display: 'flex', background: 'var(--shell-bg)', borderBottom: '2px solid var(--shell-border)', position: 'relative' }}>
        <div style={{ width: 212, flexShrink: 0, display: 'flex', position: 'sticky', left: 0, zIndex: 3, background: 'var(--shell-bg)' }}>
          <div style={{ width: 160, padding: '0 12px', fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-on-surface-2)', borderRight: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center', letterSpacing: '.3px' }}>마일스톤</div>
          <div style={{ width: 52, borderRight: 'var(--border-width-hairline) solid var(--shell-border)', flexShrink: 0 }} />
        </div>
        <div style={{ flex: 1, position: 'relative', height: milestoneRowH, overflow: 'hidden', background: `repeating-linear-gradient(90deg, transparent, transparent ${DW - 1}px, var(--shell-bg) ${DW - 1}px, var(--shell-bg) ${DW}px)` }}>
          {Array.from(milestoneDayMap.entries()).flatMap(([day, ms]) =>
            ms.map((m, i) => {
              const c = MILESTONE_COLORS[m.milestoneType ?? ''] ?? MILESTONE_DEFAULT_COLOR
              const isDraggingMs = draggingMsId === m.milestoneId
              const effectiveDay = isDraggingMs ? day + draggingMsDelta : day
              // 같은 날짜의 마일스톤은 세로로 한 줄씩 쌓아서 서로 겹치지 않게 한다
              const rowTop = ((i + 0.5) / ms.length) * milestoneRowH
              return (
                <div
                  key={m.milestoneId}
                  onMouseDown={e => onMilestoneLabelMouseDown(e, m.milestoneId, day)}
                  style={{
                    position: 'absolute', top: rowTop, left: effectiveDay * DW + DW / 2,
                    transform: 'translate(-50%, -50%)',
                    background: c, color: 'var(--shell-surface)', fontSize: 'var(--type-caption-size)', fontWeight: 700,
                    padding: '2px 8px 2px 5px', borderRadius: 12, whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: 2,
                    boxShadow: isDraggingMs ? `0 3px 10px color-mix(in srgb, ${c} 53%, transparent)` : 'var(--elevation-1)',
                    zIndex: isDraggingMs ? 10 : 1,
                    cursor: isDraggingMs ? 'grabbing' : 'grab',
                    opacity: isDraggingMs ? 0.9 : 1,
                    userSelect: 'none',
                    transition: isDraggingMs ? 'none' : 'box-shadow .15s',
                  }}
                >
                  <span style={{ fontSize: 'var(--type-caption-size)' }}>◆</span>{m.milestoneName}
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
